import React, { useContext, useEffect, useState, useRef, useCallback } from 'react'
// import { Download } from './transfer-types/download'
import { NodeCopy } from './transfer-types/copy-node'
import { TRANSFER_TYPE_COPY_NODE, TRANSFER_TYPE_CREATE_TEMPLATE } from './transfer-types/upload-base'
import Uppy from '@uppy/core'
import AwsS3 from '@uppy/aws-s3'
import { createS3MultipartAdapter } from './uppy/uppy-s3-adapter'
import { UploadItem } from './transfer-types/upload-item'
import { isTransferActive, isTransferFinished } from './transfer-status'


const Context = React.createContext()

export const useTransferContext = () => useContext(Context)

export const TransferProvider = ({ children }) => {
  
  const [items, updateItems] = useState({})

  // targetId -> Uppy instance. Lives here (not the page) so uploads survive
  // in-app navigation. One instance per target; its adapter is bound to that
  // target's endpoint group, so no per-file routing is needed.
  const uploadsRef = useRef({})

  const destroyUpload = useCallback(targetId => {
    const uppy = uploadsRef.current[targetId]
    if (!uppy) return
    uppy.destroy()
    delete uploadsRef.current[targetId]
  }, [])

  const getOrCreateUpload = useCallback(({ endpointGroup, targetId, base, path, initParams, restrictions, gotoPath }) => {
    if (uploadsRef.current[targetId]) return uploadsRef.current[targetId]

    const uppy = new Uppy({ autoProceed: false, restrictions })
    const adapter = createS3MultipartAdapter({ base, path, initParams })
    uppy.use(AwsS3, { shouldUseMultipart: true, ...adapter })

    // each added file becomes a transfer item in the shared list (the tray)
    uppy.on('file-added', file => {
      const item = new UploadItem(uppy, file, endpointGroup, targetId, gotoPath)
      insertItem(item.id, item)
    })
    // clearing/cancelling a file removes its item; empty instance is torn down
    uppy.on('file-removed', file => {
      const removedId = `${targetId}:${file.id}`
      // functional update reads LIVE state; the frozen useCallback([]) closure
      // would otherwise run the first-render handleRemoveTransfer (closes over a
      // stale `items`), wiping the whole tray incl. concurrent node-copy items.
      updateItems(state => Object.values(state).filter(t => t.id !== removedId))
      if (uppy.getFiles().length === 0) destroyUpload(targetId)
    })

    uploadsRef.current[targetId] = uppy
    return uppy
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const releaseUpload = useCallback(targetId => {
    const uppy = uploadsRef.current[targetId]
    if (uppy && uppy.getFiles().length === 0) destroyUpload(targetId)
  }, [destroyUpload])

  // Leaving the page (reload, close, another site) while an upload or a copy
  // is running asks first. The listener reads the transfers as they are now
  // (a ref): it was registered once over the first render's empty list, so
  // the warning never fired; and only running ones count, not failed ones
  // (transfer-status.js).
  const itemsRef = useRef(items)
  itemsRef.current = items

  useEffect(() => {
    const warnIfRunning = event => {
      if (!Object.values(itemsRef.current).some(isTransferActive)) return
      event.preventDefault()
      // older browsers show the prompt only for a returnValue
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warnIfRunning)
    return () => window.removeEventListener('beforeunload', warnIfRunning)
  }, [])

  const insertItem = (id, item) => {

    updateItems(state => {
      state[id] = item
      return { ...state }
    })
  }

  const handleRemoveTransfer = id => {
    let transfers = Object.values(items)
    let activeTransfers = transfers.filter(transfer => transfer.id !== id)
    updateItems(activeTransfers)
    return activeTransfers.length === 0
  }

  // "Clear finished": completed, failed and canceled ones go; running ones
  // (and files picked but not yet sent) stay.
  const handleRemoveCompletedTransfer = () => {
    let transfers = Object.values(items)
    let activeTransfers = transfers.filter(transfer => !isTransferFinished(transfer))
    updateItems(activeTransfers)
    return activeTransfers.length === 0
  }


  const handleCancelTransfer = id => {
    let item = items[id]
    if (item) {
      item.cancel()
    }
  }

  const handleNotifyClearAll = () => {
    Object.values(items).forEach(item => {
      item.notifyClear()
    })
  }

  ////////////////////////////////////////////////////////////////////////////
  ////////////////////////////////////////////////////////////////////////////

  const handleStartUpload = async id => {
    let item = items[id]
    await item.start()
  }


  const nodeCopyCourse = (id, name, pathname) => {
    const nodeCopy = new NodeCopy(id, TRANSFER_TYPE_COPY_NODE, name, process.env.REACT_APP_API_CONTENT, 'api/ddd/node-copy/ws/course-from-course', pathname)
    insertItem(id, nodeCopy)
    return nodeCopy
  }

  const nodeCopyTemplate = (id, name, pathname) => {
    const nodeCopy = new NodeCopy(id, TRANSFER_TYPE_COPY_NODE, name, process.env.REACT_APP_API_CONTENT, 'api/ddd/node-copy/ws/template-from-template', pathname)
    insertItem(id, nodeCopy)
    return nodeCopy
  }

  const nodeCopyLibrary = (id, name, pathname) => {
    const nodeCopy = new NodeCopy(id, TRANSFER_TYPE_COPY_NODE, name, process.env.REACT_APP_API_CONTENT, 'api/ddd/node-copy/ws/library-from-library', pathname)
    insertItem(id, nodeCopy)
    return nodeCopy
  }

  const nodeCopyCourseToTemplate = (id, name, pathname) => {
    const nodeCopy = new NodeCopy(id, TRANSFER_TYPE_CREATE_TEMPLATE, name, process.env.REACT_APP_API_CONTENT, 'api/ddd/node-copy/ws/template-from-course', pathname)
    insertItem(id, nodeCopy)
    return nodeCopy
  }

  const nodeCopyTemplateToCourse = (id, name, pathname) => {
    const nodeCopy = new NodeCopy(id, 'Create Course', name, process.env.REACT_APP_API_CONTENT, 'api/ddd/node-copy/ws/course-from-template', pathname)
    insertItem(id, nodeCopy)
    return nodeCopy
  }

  return (
    <>
      <Context.Provider
        value={{
          items,
          ///////////////////////////
          // handleAddUpload,
          handleStartUpload,
          getOrCreateUpload,
          releaseUpload,

          nodeCopyCourse,
          nodeCopyTemplate,
          nodeCopyLibrary,
          nodeCopyCourseToTemplate,
          nodeCopyTemplateToCourse,
          ///////////////////////////
          handleRemoveTransfer,
          handleRemoveCompletedTransfer,
          handleCancelTransfer,
          handleNotifyClearAll
        }}>
        {children}
      </Context.Provider>
    </>
  )
}
